/**
 *
 * @module coreHttpClientProvider
 *
 */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Exception from './../../exception/entry';

const ErrorReason = Exception.ErrorReason;
const ExceptionReference = Exception.ExceptionReference;
const ServiceException = Exception.ServiceException;
const InvalidArgumentException = Exception.InvalidArgumentException;
const ProviderException = Exception.ProviderException;

const {
    like
} = Check.assert;

/**
 *
 * @class CoreHttpClientProvider
 * @implements {IHttpClient}
 * @desc Core `HttpClient` provider implementation definition.
 * Acts as a base definition that is extended by platform specific implementations.
 *
 */
export default class CoreHttpClientProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(logger = Logging) {

        /**
         *
         * @access private
         * @type {Object}
         * @desc required options structure
         *
         */
        this.optionsBaseline = {
            url: ''
        };

        /**
         *
         * @access private
         * @type {String}
         * @desc message exposed if provided options dont meet baseline requirements
         *
         */
        this.optionsMismatchError = 'Options do not match expected input';

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        this.logger.log(CoreHttpClientProvider.name, 'Created Core Provider.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {ProviderException} Not implemented
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.post(options).then((response) => { console.log(response.data); });
     *
     */
    post(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return Promise.reject(new ProviderException(`${errorMessage.method} - not-implemented`));

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {ProviderException} Not implemented
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.get(options).then((response) => { console.log(response.data); });
     *
     */
    get(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return Promise.reject(new ProviderException(`${errorMessage.method} - not-implemented`));

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {ProviderException} Not implemented
     * @returns {Promise<Void>}
     *
     * @example
     * httpClient.del(options).then((response) => { console.log(response.data); });
     *
     */
    del(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return Promise.reject(new ProviderException(`${errorMessage.method} - not-implemented`));

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {ProviderException} Not implemented
     * @returns {Promise<Void>}
     *
     * @example
     * httpClient.patch(options).then((response) => { console.log(response.data); });
     *
     */
    patch(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return Promise.reject(new ProviderException(`${errorMessage.method} - not-implemented`));

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {ProviderException} Not implemented
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.put(options).then((response) => { console.log(response.data); });
     *
     */
    put(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return Promise.reject(new ProviderException(`${errorMessage.method} - not-implemented`));

    }

    /* #region private: */

    /**
     *
     * @access private
     * @method getResponseData
     * @param {*} responseData
     * @desc if the response cannot be parsed, provide the response as a plain String
     * @returns {Object|String}
     *
     */
    getResponseData(responseData) {

        let data;

        try {

            data = JSON.parse(responseData);

        } catch (error) {

            data = responseData;

        }

        return data;

    }

    /**
     *
     * @access private
     * @param {Number} status
     * @desc Allow status codes to pass through to the client
     * implementations where they are expected to be handled
     * @returns {Boolean}
     *
     */
    verifyStatus(status) {

        if (status >= 200) {

            return true;

        } else {

            return false;

        }

    }

    /**
     *
     * @access private
     * @param {Object} error
     * @returns {Promise<NetworkException>}
     * @desc Fetch API "will only reject on network failure or
     * if anything prevented the request from completing."
     * Therefore, reject a `NetworkException`. 4xx and 5xx responses
     * "will resolve normally (with ok status set to false)"
     * Handle them further down the Promise chain.
     *
     */
    onError(error) {

        const exceptionData = ExceptionReference.common.network;

        let reasons = [];

        if (Check.assigned(error)) {
            reasons = [new ErrorReason(error.name, error.message)];
        }

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/shared/coreHttpClientProvider.js